/*
 * Feature flags: the Rollout tab of the Feature Detail modal (audit Phase 3.3).
 * Replaces wwwroot/js/Feature/Rollout.js. No jQuery.
 *
 * A plan is an ordered set of stages; running a stage writes ordinary bindings
 * (the opposite of the feature's default) and reverting one deletes them, so
 * nothing here bypasses the Bindings tab. Every write goes to the Web
 * controller's Plan routes and the whole tab re-renders from
 * Feature/{id}/Rollout -- which is also how the Bindings tab learns a stage
 * moved underneath it.
 */
import { ajaxJson, failToast } from '../core/ajax';
import { el } from '../core/dom';
import { serialize } from '../core/forms';
import type { NameValue } from '../core/http';
import { confirm } from '../core/modal';
import { toast } from '../core/toast';
import {
    BINDINGS_PANE, ROLLOUT_PANE, markListDirty, markStale, reloadTab,
} from './feature';

const TRIGGER_FIELDS: Record<string, string> = {
    Scheduled: '.feature-stage-when',
    ResourceProperty: '.feature-stage-claim',
    Percent: '.feature-stage-percent',
};

/**
 * The plan whose Add-stage panel was open when the tab last redrew itself.
 *
 * Every write re-renders the whole tab, and a <details> comes back closed --
 * so adding the second stage of a plan meant re-opening the panel, re-picking
 * the trigger and re-finding the place every single time. The panel is put
 * back where the operator left it instead.
 */
let openPlanId = 0;

function stageForm(planId: number | string): HTMLFormElement | null {
    return document.getElementById(`FeatureStageForm_${planId}`) as HTMLFormElement | null;
}

function value(id: string): string {
    return ((document.getElementById(id) as HTMLInputElement | null)?.value ?? '').trim();
}

function pickedId(id: string): number {
    const parsed = Number.parseInt(value(id), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/** The display half of an entity picker pair, so a chip can be labelled with what was picked. */
function pickedLabel(id: string): string {
    const hidden = document.getElementById(id);
    const display = hidden?.closest('.entity-picker-control')?.querySelector('.entity-picker-display');
    return (display as HTMLInputElement | null)?.value ?? '';
}

function clearPicker(id: string): void {
    const hidden = document.getElementById(id) as HTMLInputElement | null;
    if (!hidden) return;
    hidden.value = '';
    const display = hidden.closest('.entity-picker-control')?.querySelector('.entity-picker-display') as HTMLInputElement | null;
    if (display) display.value = '';
}

export const Rollout = {
    CreatePlan(featureId: number | string): void {
        const name = value(`FeaturePlanName_${featureId}`);
        if (!name) {
            toast('warning', 'Plan name', 'Give the plan a name.');
            return;
        }
        write(featureId, `Feature/${featureId}/Plan`, 'POST', [
            { name: 'Name', value: name },
            { name: 'Description', value: value(`FeaturePlanDescription_${featureId}`) },
        ], 'Plan created', 'Failed to create plan');
    },

    DeletePlan(featureId: number | string, planId: number): void {
        void confirm({
            title: 'Delete plan',
            html: 'Delete this plan?<br/><br/>Bindings its stages already wrote <b>stay</b> -- revert the stages first if you want them undone.',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => write(featureId, `Feature/${featureId}/Plan/${planId}`, 'DELETE', null, 'Plan deleted', 'Failed to delete plan'),
        });
    },

    Activate(featureId: number | string, planId: number): void {
        write(featureId, `Feature/${featureId}/Plan/${planId}/Activate`, 'POST', null, 'Plan active', 'Failed to activate plan');
    },

    Pause(featureId: number | string, planId: number): void {
        write(featureId, `Feature/${featureId}/Plan/${planId}/Pause`, 'POST', null, 'Plan paused', 'Failed to pause plan');
    },

    /** The trigger select decides which extra fields a stage needs. */
    TriggerChanged(planId: number | string): void {
        const form = stageForm(planId);
        if (form) applyTrigger(form);
    },

    /**
     * Move the picker's selection into the stage's target list as hidden
     * Targets[n].* inputs. The chip is the visible half of the same thing:
     * remove the chip and the inputs go with it.
     */
    AddTarget(planId: number | string, isPersonLevel?: unknown): void {
        const form = stageForm(planId);
        const box = document.getElementById(`FeatureStageTargets_${planId}`);
        if (!form || !box) return;
        const targetId = pickedId(`FeatureStageTarget_${planId}`);
        const groupId = pickedId(`FeatureStageGroup_${planId}`);
        const personLevel = isPersonLevel === true || isPersonLevel === 'true';
        if (!targetId && !groupId) {
            toast('warning', 'Pick a target', 'Use the search button to pick what this stage switches.');
            return;
        }
        if (targetId && groupId) {
            toast('warning', 'One target', 'Pick either a person or a person group, not both.');
            return;
        }

        const index = Number.parseInt(form.getAttribute('data-target-count') ?? '0', 10) || 0;
        const resourceId = personLevel ? 0 : targetId;
        const personId = personLevel ? targetId : 0;
        const label = groupId
            ? pickedLabel(`FeatureStageGroup_${planId}`)
            : pickedLabel(`FeatureStageTarget_${planId}`);
        const key = `r${resourceId || ''}p${personId || ''}g${groupId || ''}`;
        if (form.querySelector(`.feature-stage-target[data-key="${key}"]`)) {
            toast('warning', 'Already listed', 'That target is already in this stage.');
            return;
        }

        const chip = el('span', 'badge mcp-chip bg-body-secondary text-body border me-1 mb-1 feature-stage-target');
        chip.setAttribute('data-key', key);
        chip.appendChild(document.createTextNode(label || `#${targetId || groupId}`));
        // The x is a real button carrying a data-action rather than an <i> with a
        // listener bound straight to it: Font Awesome replaces every <i> it finds
        // with an <svg>, and a handler attached to the element it replaced goes
        // with it. That is why the legacy chips could not be removed at all.
        const remove = el('button', 'btn mcp-chip-remove text-danger');
        remove.type = 'button';
        remove.title = 'Remove';
        remove.setAttribute('data-action', 'Feature.Rollout.RemoveTarget');
        remove.appendChild(el('i', 'fas fa-times'));
        chip.appendChild(remove);
        for (const [suffix, id] of [['TargetResourceId', resourceId], ['TargetPersonId', personId], ['TargetPersonGroupId', groupId]] as const) {
            const input = el('input');
            input.type = 'hidden';
            input.name = `Targets[${index}].${suffix}`;
            input.value = id ? String(id) : '';
            chip.appendChild(input);
        }
        box.appendChild(chip);
        form.setAttribute('data-target-count', String(index + 1));

        clearPicker(`FeatureStageTarget_${planId}`);
        clearPicker(`FeatureStageGroup_${planId}`);
        emptyCheck(box);
    },

    /** `this` is the x inside the chip being taken out of the stage. */
    RemoveTarget(this: Element): void {
        const chip = this.closest('.feature-stage-target');
        const box = chip?.parentElement;
        chip?.remove();
        if (box) emptyCheck(box);
    },

    AddStage(featureId: number | string, planId: number | string): void {
        const form = stageForm(planId);
        if (!form) return;
        const name = (form.querySelector('input[name=Name]') as HTMLInputElement | null)?.value?.trim() ?? '';
        if (!name) {
            toast('warning', 'Stage name', 'Give the stage a name.');
            return;
        }
        const targets = Array.from(form.querySelectorAll('.feature-stage-target'));
        if (targets.length === 0) {
            toast('warning', 'No targets', 'Add at least one target to the stage.');
            return;
        }
        const trigger = (form.querySelector('select[name=Trigger]') as HTMLSelectElement | null)?.value ?? '';
        if (trigger === 'Percent') {
            const percent = Number.parseInt((form.querySelector('input[name=TriggerPercent]') as HTMLInputElement | null)?.value ?? '', 10);
            if (!(percent >= 1 && percent <= 100)) {
                toast('warning', 'Percentage', 'A percentage stage needs a whole percentage from 1 to 100.');
                return;
            }
        }
        if (trigger === 'Scheduled' && !(form.querySelector('input[name=TriggerAt]') as HTMLInputElement | null)?.value) {
            toast('warning', 'Fires at', 'A scheduled stage needs the time it fires at.');
            return;
        }
        if (trigger === 'ResourceProperty' && !(form.querySelector('input[name=TriggerClaimType]') as HTMLInputElement | null)?.value?.trim()) {
            toast('warning', 'Setting', 'A setting stage needs the claim type to look for.');
            return;
        }

        // Renumber so MVC sees a contiguous Targets[0..n-1]: a chip removed from
        // the middle leaves a hole, and the binder stops at the first one.
        targets.forEach((chip, index) => {
            for (const input of chip.querySelectorAll('input[type=hidden]')) {
                input.setAttribute('name', (input.getAttribute('name') ?? '').replace(/Targets\[\d+\]/, `Targets[${index}]`));
            }
        });
        form.setAttribute('data-target-count', String(targets.length));

        openPlanId = Number(planId) || 0;
        write(featureId, `Feature/${featureId}/Plan/${planId}/Stage`, 'POST', serialize(form), 'Stage added', 'Failed to add stage');
    },

    /**
     * `order` is the plan's pending stage ids in their new order, computed by
     * the view. The Api refuses anything that is not exactly the pending set,
     * so a stale tab is told so rather than silently reordering the wrong
     * stages.
     */
    Reorder(featureId: number | string, planId: number, order: number[]): void {
        // Indexed names, built by hand. core/http's encodeParams spells an array
        // `StageIds[]=2&StageIds[]=1` -- the PHP-style empty-bracket form, which
        // ASP.NET Core's binder does not read -- so List<int> would arrive EMPTY
        // and the Api would refuse with "name every pending stage".
        const data: NameValue[] = (order ?? []).map((id, index) => ({ name: `StageIds[${index}]`, value: String(id) }));
        write(featureId, `Feature/${featureId}/Plan/${planId}/Stage/Order`, 'PUT', data, 'Stage order updated', 'Failed to reorder stages');
    },

    DeleteStage(featureId: number | string, planId: number, stageId: number): void {
        write(featureId, `Feature/${featureId}/Plan/${planId}/Stage/${stageId}`, 'DELETE', null, 'Stage removed', 'Failed to remove stage');
    },

    /**
     * `percent` is the stage's TriggerPercent, or null for every other trigger
     * kind: a percentage stage binds a slice of the people it names, not all of
     * them, and the confirmation has to say which of the two is about to happen.
     */
    Run(featureId: number | string, planId: number, stageId: number, percent?: number | null): void {
        void confirm({
            title: 'Run stage',
            html: percent
                ? `Apply this stage now? The ${percent}% of the people it names who fall in the cohort get a binding; `
                  + 'the rest are left on the default.<br/><br/>This is audited.'
                : "Apply this stage now? Every target it names gets a binding, whatever the stage's trigger says."
                  + '<br/><br/>This is audited.',
            confirmText: 'Run',
            variant: 'success',
            onConfirm: () => write(featureId, `Feature/${featureId}/Plan/${planId}/Stage/${stageId}/Run`, 'POST', null, 'Stage applied', 'Failed to run stage'),
        });
    },

    Revert(featureId: number | string, planId: number, stageId: number): void {
        void confirm({
            title: 'Revert stage',
            html: "Remove every binding this stage wrote and pause the plan? Targets revert to the feature's default."
                + '<br/><br/>This is audited.',
            confirmText: 'Revert',
            variant: 'warning',
            onConfirm: () => write(featureId, `Feature/${featureId}/Plan/${planId}/Stage/${stageId}/Revert`, 'POST', null, 'Stage reverted', 'Failed to revert stage'),
        });
    },

    /**
     * Redraw the tab. A stage may have written or removed bindings, so the
     * Bindings tab must refetch when next shown and the list behind the modal
     * when the dialog closes.
     */
    Reload(): Promise<void> {
        return reloadTab(ROLLOUT_PANE).then(() => {
            markStale(BINDINGS_PANE);
            markListDirty();
            reopenStagePanel();
        });
    },
};

/**
 * Apply one rendering of a stage form's trigger rule.
 *
 * The jQuery original hung this off a document-level change handler and
 * nothing else, so it ran only on a change the operator made. Registered as an
 * enhancer it also runs on arrival, which is what a tab that re-renders itself
 * after every write needs.
 */
export function enhanceStageForms(root: ParentNode): void {
    const forms: Element[] = [];
    if (root instanceof Element && root.matches('.feature-stage-form')) forms.push(root);
    forms.push(...Array.from(root.querySelectorAll('.feature-stage-form')));
    for (const form of forms) {
        applyTrigger(form);
        emptyCheck(form.querySelector('.feature-stage-targets'));
    }
}

// ---------------------------------------------------------------- helpers

function applyTrigger(form: Element): void {
    const kind = (form.querySelector('select[name=Trigger]') as HTMLSelectElement | null)?.value ?? '';
    for (const [trigger, selector] of Object.entries(TRIGGER_FIELDS)) {
        for (const node of form.querySelectorAll(selector)) node.classList.toggle('d-none', kind !== trigger);
    }
}

function emptyCheck(box: Element | null | undefined): void {
    if (!box) return;
    const empty = box.querySelector('.feature-stage-targets-empty');
    empty?.classList.toggle('d-none', box.querySelectorAll('.feature-stage-target').length > 0);
}

/** Put the Add-stage panel back where the operator left it after a redraw. */
function reopenStagePanel(): void {
    if (!openPlanId) return;
    const panel = stageForm(openPlanId)?.closest('details');
    openPlanId = 0;
    if (panel) panel.setAttribute('open', '');
}

function write(
    featureId: number | string,
    url: string,
    method: string,
    body: NameValue[] | null,
    successTitle: string,
    errorTitle: string,
): void {
    void ajaxJson({
        url,
        method,
        data: body,
        alertInto: `FeatureRolloutMessageContainer_${featureId}`,
        errorTitle,
        onSuccess: () => {
            toast('success', successTitle);
            void Rollout.Reload();
        },
        onFailure: data => {
            failToast(errorTitle)(data);
            // A refusal usually means the plan moved underneath the tab (the
            // ticker fired a stage, another operator paused it): show what is
            // true now rather than leaving a view that has been overtaken.
            void Rollout.Reload();
            return true;
        },
    });
}

/** Test support: the module remembers which plan's panel to re-open across a redraw. */
export function resetStagePanel(): void {
    openPlanId = 0;
}
